'use client';

import { cn } from '@module/lib/utils';
import * as React from 'react';

export interface ToggleButtonProps {
  /** 토글 상태 */
  checked: boolean;
  /** 토글 상태 변경 핸들러 */
  onChange: (checked: boolean) => void;
  /** 비활성화 여부 */
  disabled?: boolean;
  /** 버튼 내부 텍스트 */
  children?: React.ReactNode;
  /** 커스텀 클래스명 */
  className?: string;
  /** 접근성을 위한 aria-label */
  'aria-label'?: string;
}

export const ToggleButton = React.forwardRef<HTMLButtonElement, ToggleButtonProps>(
  ({ checked, onChange, disabled = false, children, className, 'aria-label': ariaLabel, ...props }, ref) => {
    const handleToggle = React.useCallback(() => {
      if (!disabled) {
        onChange(!checked);
      }
    }, [checked, disabled, onChange]);

    return (
      <button
        ref={ref}
        type='button'
        role='switch'
        aria-checked={checked}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={handleToggle}
        className={cn(
          // 기본 타원형 스타일
          'inline-flex items-center justify-center rounded-[20px] px-4 py-2 focus:outline-none',
          // 상태별 스타일 (기본값, 구체적 디자인은 className으로 override)
          {
            'bg-colorBg10 font-medium text-colorFg05': checked && !disabled,
            'border bg-colorBg09 text-[#777] ': !checked && !disabled,
            'cursor-not-allowed bg-gray-100 text-gray-400': disabled,
          },
          className,
        )}
        {...props}
      >
        {children}
      </button>
    );
  },
);

ToggleButton.displayName = 'ToggleButton';

export default ToggleButton;
